import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp,
  DollarSign,
  FolderKanban,
  Users,
  AlertTriangle,
  ArrowRight,
  Clock,
} from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { KpiCard } from '@/components/kpi-card';
import {
  fetchProjects,
  fetchPortfolioSummary,
  fetchUtilisationData,
  fetchClients,
  fetchEmployees,
} from '@/lib/data-service';
import { useErrorHandler } from '@/hooks/use-error-handler';
import {
  formatCurrency,
  formatPercent,
  getHealthBadgeColor,
  getStatusColor,
  formatStatus,
  getVarianceColor,
} from '@/lib/utils';
import type { Project, PortfolioSummary, UtilisationData, Client, Employee } from '@/types';

export function DashboardPage({ onNavigate }: { onNavigate?: (page: string) => void } = {}) {
  const navigateHook = useNavigate();
  const handleNavigate = onNavigate || ((page: string) => navigateHook(`/${page}`));
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [utilisation, setUtilisation] = useState<UtilisationData[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const { handleApiError } = useErrorHandler({ useToast: true });

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [projectsData, portfolioData, utilisationData, clientsData, employeesData] =
          await Promise.all([
            fetchProjects('dashboard'),
            fetchPortfolioSummary('dashboard'),
            fetchUtilisationData('dashboard'),
            fetchClients('dashboard'),
            fetchEmployees('dashboard'),
          ]);
        setProjects(projectsData);
        setPortfolio(portfolioData);
        setUtilisation(utilisationData);
        setClients(clientsData);
        setEmployees(employeesData);
      } catch (error) {
        handleApiError(error, '/api/dashboard', 'GET');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [handleApiError]);

  const activeProjects = projects.filter(p => p.status === 'in-progress');
  const atRiskProjects = activeProjects.filter(p => p.health === 'amber' || p.health === 'red');
  const avgUtilisation =
    utilisation.reduce((sum, u) => sum + u.utilisationPercent, 0) / utilisation.length || 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="transition-all hover:shadow-md">
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-3 w-28" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Data Sensitivity Toggle moved to header bar */}

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Active Projects"
          value={portfolio?.activeProjects || 0}
          icon={FolderKanban}
          description={`${atRiskProjects.length} at risk`}
        />

        <KpiCard
          title="Portfolio Value"
          value={
            <SensitiveData>
              {formatCurrency(portfolio?.portfolioValue || 0)}
            </SensitiveData>
          }
          icon={DollarSign}
          description="Total contract value"
        />

        <KpiCard
          title="Avg Margin"
          value={
            <SensitiveData>
              {formatPercent(portfolio?.avgMarginPercent || 0)}
            </SensitiveData>
          }
          icon={TrendingUp}
          trend={{
            value: 2.5,
            label: '%',
            isPositive: true,
          }}
          description="Above target"
        />

        <KpiCard
          title="Team Utilisation"
          value={formatPercent(avgUtilisation, 0)}
          icon={Users}
          description="Target: 75%"
          trend={{
            value: avgUtilisation - 75,
            label: '%',
            isPositive: avgUtilisation >= 75,
          }}
        />
      </div>

      {/* At Risk Projects Alert */}
      {atRiskProjects.length > 0 && (
        <Card className="border-warning-200 bg-warning-50 dark:bg-warning-950/20">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning-600" />
              {atRiskProjects.length} Project{atRiskProjects.length > 1 ? 's' : ''} Need Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
                {atRiskProjects.slice(0, 3).map(project => {
                  // Get first client from clientIds (supports multiple clients)
                  const firstClientId = project.clientIds?.split(',')[0];
                  const client = firstClientId ? clients.find(c => c.id === firstClientId) : undefined;
                  return (
                  <div
                    key={project.id}
                    className="flex items-center justify-between p-3 bg-card dark:bg-card rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className={getHealthBadgeColor(project.health)}>
                          {project.health.toUpperCase()}
                        </Badge>
                        <span className="font-medium">{project.name}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {client?.name} • {formatPercent(project.completionPercent, 0)} complete
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleNavigate('projects')}>
                      View <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project Health Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Project Health</CardTitle>
            <Button variant="outline" size="sm" onClick={() => handleNavigate('projects')}>
              View All Projects
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
              {activeProjects.slice(0, 10).map(project => {
                // Get client name - use clientNames if available, otherwise look up first client
                const clientName = project.clientNames || (project.clientIds?.split(',')[0] ? clients.find(c => c.id === project.clientIds?.split(',')[0])?.name : undefined);
                const manager = employees.find(e => e.id === project.projectManagerId);
              
              return (
                <div key={project.id} className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className={getHealthBadgeColor(project.health)}>
                          {project.health.toUpperCase()}
                        </Badge>
                        <span className="font-medium">{project.name}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span>{clientName}</span>
                        <span>•</span>
                        <span>{manager?.name}</span>
                        <span>•</span>
                        <Badge variant="outline" className={getStatusColor(project.status)}>
                          {formatStatus(project.status)}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="text-sm font-medium">
                        <SensitiveData>
                          {formatCurrency(project.budgetRevenue)}
                        </SensitiveData>
                      </div>
                      <div className={`text-xs ${getVarianceColor(project.variancePercent)}`}>
                        {project.variancePercent > 0 ? '+' : ''}
                        {formatPercent(project.variancePercent)} variance
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Progress value={project.completionPercent} className="h-2" />
                    </div>
                    <span className="text-xs text-muted-foreground w-12 text-right">
                      {formatPercent(project.completionPercent, 0)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="cursor-pointer hover:bg-accent transition-colors" onClick={() => handleNavigate('utilisation')}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-info-600" />
              View Utilisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Track team billable hours and capacity
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-accent transition-colors" onClick={() => handleNavigate('wip-reporting')}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-5 w-5 text-success-600" />
              WIP Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Month-end work in progress analysis
            </p>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:bg-accent transition-colors" onClick={() => handleNavigate('profitability')}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-purple-600" />
              Profitability
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Real-time project margin analysis
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
